import styled from 'styled-components';
import React, { useMemo, useState } from 'react';
import {
  ArrowCounterClockwise,
  ArrowsSplit,
  ArrowUpRight,
  Check,
  Circle,
  File,
  ListChecks,
  ListDashes,
  MapPin,
  Package,
  Question,
  Receipt,
  StackSimple,
  Tote,
  Waves,
  XCircle,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import {
  Image,
  Placeholder,
  PlaceholderLine,
  PlaceholderParagraph,
  Popup,
  Table,
  TableRow,
} from 'semantic-ui-react';
import {
  ActionEditDots,
  ActionMenuItem,
  AppTable,
  ItemImage,
  OrganisationAvatar,
  SimpleItemImage,
} from '../../../components';
import { Border, ColoredPill, MuteText, ShadowBox } from '../../../elements';
import { CommonUtility } from '../../../utility/common';
import { carrierImageMap, OrderStatus, theme } from '../../../utility';
import { ExpandCollapseToggle } from '@page-components/shipments/Items/commonComponents';

const ShadowBoxHeader = styled.div`
  margin: 0;
  padding-bottom: 1em;
`;

const Header = styled.div`
  font-size: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.primary};
  padding-bottom: 10px;
  margin-top: 10px;
`;
const FitShadowBox = styled(ShadowBox)`
  &&& {
    height: auto !important;
    align-self: flex-start !important;
  }
`;
export function LabelDetails({ data, loading, openRefundPopup, openLabelCorrection }) {
  return (
    <FitShadowBox className="col-5 p-3">
      {loading ? (
        <div className="p-4">
          <Placeholder fluid>
            <PlaceholderParagraph>
              <PlaceholderLine />
            </PlaceholderParagraph>
            <PlaceholderParagraph>
              <PlaceholderLine />
              <PlaceholderLine />
              <PlaceholderLine />
              <PlaceholderLine />
            </PlaceholderParagraph>
          </Placeholder>
        </div>
      ) : (
        <>
          <ShadowBoxHeader className="pt-4">
            <Title>
              <Receipt size={25} color="#ACADB6" className="mr-2" />
              Labels
            </Title>
          </ShadowBoxHeader>
          <Header className="row">
            <div className="col-4">
              <MuteText>Carrier</MuteText>
            </div>
            <div className="col-2">
              <MuteText>Tracking</MuteText>
            </div>
            <div className="col-3">
              <MuteText>Dimensions</MuteText>
            </div>
            <div className="col-3">
              <MuteText>Label</MuteText>
            </div>
          </Header>
          {data?.labels?.map((label, index) => (
            <LabelRow
              itemsData={data?.items}
              key={label.tracking_id}
              totalLabels={data?.labels?.length}
              item={label}
              index={index}
              openRefundPopup={openRefundPopup}
              openLabelCorrection={openLabelCorrection}
            />
          ))}
          <div className="mb-2" />
        </>
      )}
    </FitShadowBox>
  );
}

const ActionDiv = styled.div`
  &:hover {
    .action-container {
      opacity: 1 !important;
    }
  }

  &.show-border {
    border-top: 1px solid ${({ theme }) => theme.colors.box};
  }
`;

const ItemsContaner = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.box};
  border-radius: 5px;
  margin: 5px 15px;
  margin-bottom: 10px;
`;

function initials(name = '') {
  const parts = name.trim().split(/\s+/);
  const a = (parts[0]?.[0] || '').toUpperCase();
  const b = (parts[1]?.[0] || '').toUpperCase();
  return (a + (b || '')).slice(0, 2) || 'NA';
}

function LabelRow({ item, itemsData, openRefundPopup, openLabelCorrection, index }) {
  return (
    <>
      <ActionDiv className={`row py-2 ${index !== 0 ? 'show-border' : ''}`}>
        <div className="col-4 d-flex">
          {carrierImageMap[item.carrier_code] ? (
            <CarrierLogo src={carrierImageMap[item.carrier_code]} />
          ) : (
            <OrganisationAvatar
              name={CommonUtility.removeUnderscoreAndCapitalize(item.carrier_code)}
            />
          )}
          <div className="d-flex flex-column">
            <b>{CommonUtility.removeUnderscoreAndCapitalize(item.carrier_code)}</b>
            <MuteText>
              {CommonUtility.removeUnderscoreAndCapitalize(item.service.value)}
            </MuteText>
          </div>
        </div>
        <div className="col-2 pt-2 d-flex align-items-center">
          <TrackingId>{item.tracking_id}</TrackingId>
        </div>
        <div className="col-3 pt-2">
          <div className="d-flex justify-content-left flex-wrap">
            <span>{CommonUtility.roundNumber(item?.dimensions?.length?.value)}</span>
            <MuteText className="mx-1">x</MuteText>
            <span>{CommonUtility.roundNumber(item?.dimensions?.width?.value)}</span>
            <MuteText className="mx-1">x</MuteText>
            <span>{CommonUtility.roundNumber(item?.dimensions?.height?.value)}</span>
            <MuteText className="ml-1">({item?.dimensions?.width?.unit}.)</MuteText>
          </div>
          <div className="d-flex justify-content-left flex-wrap col-gap-1 p-0">
            <div>
              <span>{CommonUtility.roundNumber(item?.weight?.pounds)}</span>
              <MuteText className="ml-1">lbs</MuteText>
            </div>
            <div>
              <span>{CommonUtility.roundNumber(item?.weight?.ounces)}</span>
              <MuteText className="ml-1">oz</MuteText>
            </div>
          </div>
        </div>
        <div className="col-3">
          <ActionEditDots preventChangingBg on="hover" position="left center">
            <ActionMenuItem
              onClick={() => {
                openRefundPopup(item);
              }}
            >
              <ArrowCounterClockwise />
              Refund
            </ActionMenuItem>
            <ActionMenuItem
              onClick={() => {
                openLabelCorrection(item);
              }}
            >
              <Receipt />
              Correct Label
            </ActionMenuItem>
          </ActionEditDots>
          <FileContainer className="mr-4" href={item.url} download target="_blank">
            <File className="mr-2" size={18} />
            <span>View Label</span>
          </FileContainer>
        </div>
      </ActionDiv>
      <ItemsContaner>
        {item?.items?.map((itemDetail, index) => (
          <ItemRow
            item={itemDetail}
            itemsData={itemsData}
            key={itemDetail.item_id}
            index={index}
          />
        ))}
      </ItemsContaner>
    </>
  );
}

function ItemRow({ item, itemsData, index }) {
  const itemDetail = useMemo(
    () => itemsData.find(x => x.item_id === item.item_id),
    [item, itemsData],
  );

  return (
    <ItemContainer className={`row ${index > 0 ? 'showBorder' : ''}`}>
      <div className="col-8">
        <div className="d-flex">
          <ItemImage item={itemDetail} size="mini" />
          <ItemTitle className="ml-2">
            <b>{itemDetail?.title}</b>
            <div>
              <MuteText>SKU</MuteText>
              <span className="ml-1">{itemDetail?.sku}</span>
            </div>
          </ItemTitle>
        </div>
      </div>
      <div className="col-2 pt-2">
        {CommonUtility.currencyFormat(itemDetail?.itemPrice)}
      </div>
      <div className="last-cell col-2 pt-2">
        <span className="ml-1">x {item?.qty}</span>
      </div>
    </ItemContainer>
  );
}

const ACTION_TO_UI = {
  created: { label: 'Created', Icon: StackSimple, color: theme.colors.green },
  cancelled: { label: 'Cancelled', Icon: XCircle, color: theme.colors.red },
  enqueued: {
    label: 'Allocated',
    Icon: ArrowsSplit,
    color: theme.colors.green,
  },
  allocated: {
    label: 'Allocated',
    Icon: ArrowsSplit,
    color: theme.colors.green,
  },
  sorted: { label: 'Sorted', Icon: Tote, color: theme.colors.green },
  packed: { label: 'Packing', Icon: Package, color: theme.colors.green },
  packing: { label: 'Packing', Icon: Package, color: theme.colors.green },
  shipped: { label: 'Completed', Icon: Check, color: theme.colors.grey },
  completed: { label: 'Completed', Icon: Check, color: theme.colors.grey },
  added_to_wave: {
    label: 'Added to Wave',
    Icon: ListDashes,
    color: theme.colors.green,
  },
  removed_from_wave: {
    label: 'Removed from Wave',
    Icon: ListDashes,
    color: theme.colors.orange,
  },
};

const norm = s =>
  String(s || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const STATUS_LABEL_TO_KEY = {
  created: 'created',
  allocated: 'allocated',
  sorted: 'sorted',
  packing: 'packing',
  completed: 'completed',
  added_to_wave: 'added_to_wave',
  removed_from_wave: 'removed_from_wave',
};

const resolveKey = status => {
  const s = norm(status);

  if (ACTION_TO_UI[s]) return s;

  if (STATUS_LABEL_TO_KEY[s]) return STATUS_LABEL_TO_KEY[s];

  if (s) return s;

  return 'unknown';
};

function StatusCell({ status }) {
  const key = resolveKey(status);
  const conf = ACTION_TO_UI[key];
  const label = conf?.label || status || 'Unknown';
  const color = conf?.color || theme.colors.grey;
  const Icon = conf?.Icon || Question;

  return (
    <div className="d-flex align-items-center">
      <Icon size={20} color={color} className="mr-2" />
      <span>{label}</span>
    </div>
  );
}

function LogRowTable({ logs }) {
  const sorted = (Array.isArray(logs) ? [...logs] : []).sort(
    (a, b) => new Date(a?.timestamp || 0) - new Date(b?.timestamp || 0),
  );

  return (
    <Table basic compact="very">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Timestamp</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.HeaderCell>User</Table.HeaderCell>
          <Table.HeaderCell>Details</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {sorted.length ? (
          sorted.map((item, i) => (
            <Table.Row key={item.id || i} verticalAlign="middle" style={{ height: 30 }}>
              <Table.Cell style={{ paddingTop: 4, paddingBottom: 4 }}>
                {item.timestamp ? (
                  <>
                    {moment(item.timestamp).format("MMM DD, 'YY")}
                    <MuteText className="ml-2">
                      {moment(item.timestamp).format('hh:mm A')}
                    </MuteText>
                  </>
                ) : (
                  <MuteText>—</MuteText>
                )}
              </Table.Cell>
              <Table.Cell style={{ paddingTop: 4, paddingBottom: 4 }}>
                <StatusCell status={item.status} />
              </Table.Cell>
              <Table.Cell style={{ paddingTop: 4, paddingBottom: 4 }}>
                <div className="d-flex align-items-center">
                  <UserAvatar user={item?.user} />
                  <span>{item?.user?.name}</span>
                </div>
              </Table.Cell>

              <Table.Cell style={{ paddingTop: 4, paddingBottom: 4 }}>
                <LogsDetailsCell log={item} />
              </Table.Cell>
            </Table.Row>
          ))
        ) : (
          <Table.Row>
            <Table.Cell colSpan={4}>
              <MuteText>No logs</MuteText>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table>
  );
}

function UserAvatar({ user }) {
  const [broken, setBroken] = useState(false);
  const src =
    user?.image || user?.avatar || user?.avatarUrl || user?.photoUrl || user?.picture;

  if (src && !broken) {
    return (
      <AvatarImg src={src} alt={user?.name || 'user'} onError={() => setBroken(true)} />
    );
  }
  return <UserBubble>{initials(user?.name)}</UserBubble>;
}

const PillRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

function buildCompletedUrl(containerId, warehouseId) {
  const q = warehouseId ? `?warehouseId=${warehouseId}` : '';
  return `/app/fulfill/packing/completed/${containerId}/${q}`;
}

function LogsDetailsCell({ log }) {
  const chips = [];

  if (log?.tote) {
    const cId = log?.tote?.completedContainerId;
    const wId = log?.tote?.warehouseId;
    const pill = (
      <ColoredPill key="tote" color={theme.colors.lighterGrey}>
        <Tote size={16} className="mr-1" /> {log?.tote?.toteNumber}
      </ColoredPill>
    );
    chips.push(
      cId ? (
        <PillLink key="tote-link" to={buildCompletedUrl(cId, wId)}>
          {pill}
        </PillLink>
      ) : (
        pill
      ),
    );
  }

  if (log?.pallet) {
    const cId = log?.pallet?.completedContainerId;
    const wId = log?.pallet?.warehouseId;
    const pill = (
      <ColoredPill key="pallet" color={theme.colors.lighterGrey}>
        <StackSimple size={16} className="mr-1" /> {log?.pallet?.palletNumber}
      </ColoredPill>
    );
    chips.push(
      cId ? (
        <PillLink key="pallet-link" to={buildCompletedUrl(cId, wId)}>
          {pill}
        </PillLink>
      ) : (
        pill
      ),
    );
  }

  if (log?.package) {
    chips.push(
      <ColoredPill key="package" color={theme.colors.lighterGrey}>
        <Package size={16} className="mr-1" /> Package
      </ColoredPill>,
    );
  }

  return chips.length ? <PillRow>{chips}</PillRow> : null;
}

const ActionEditTableRow = styled(TableRow)`
  position: relative;

  .action-container {
    margin: auto;
    background: transparent;
    right: 10px;
    bottom: 0;
  }

  &:hover {
    .action-container {
      opacity: 1 !important;
    }
  }

  .toggle {
    margin: auto 0;
    margin-left: auto;
  }
`;

function PickingRow({ item, index, cancelAllocation }) {
  const [open, setOpen] = useState(false);
  const product = item?.inventory?.product;
  const img = product?.images?.display_image;
  const name = product?.name || '—';
  const sku = product?.sku || '—';
  const locationCode = item?.inventory?.location?.code || '—';
  const status = item?.logs?.[item?.logs?.length - 1]?.status || '—';
  const key = item?.logs?.[item?.logs?.length - 1]?.action || '—';

  const conf = ACTION_TO_UI[key];
  const label = conf?.label || status || 'Unknown';
  const color = conf?.color || theme.colors.grey;

  const logs = Array.isArray(item?.logs) ? item.logs : [];

  const allowCancelling = useMemo(
    () => item.logs.every(x => x.action !== 'cancelled'),
    [item],
  );

  return (
    <>
      <ActionEditTableRow
        key={`${item?.allocationId || index}-main`}
        verticalAlign="middle"
      >
        <Table.Cell textAlign="left">
          <ItemName>
            <SimpleItemImage size="mini" src={img} />
            <ItemMeta>
              <MuteText>{name}</MuteText>
              <SkuRow>
                <span>{sku}</span>
              </SkuRow>
            </ItemMeta>
            <ExpandCollapseToggle
              className="toggle"
              title=""
              expandText="Show Logs"
              collapseText="Hide Logs"
              open={open}
              setOpen={setOpen}
            />
          </ItemName>
        </Table.Cell>
        <Table.Cell textAlign="left">
          <ColoredPill mini color={theme.colors.pink} className="text-nowrap">
            <MapPin /> {locationCode}
          </ColoredPill>
        </Table.Cell>
        <Table.Cell textAlign="left">
          <ColoredPill color={color} mini>
            <Circle weight="fill" />
            {label}
          </ColoredPill>
          {allowCancelling && (
            <ActionEditDots preventChangingBg on="hover" position="left center">
              <ActionMenuItem
                onClick={() => {
                  cancelAllocation(item);
                }}
              >
                <XCircle />
                Cancel Allocation
              </ActionMenuItem>
            </ActionEditDots>
          )}
        </Table.Cell>
      </ActionEditTableRow>

      {open && (
        <Table.Row key={`${item?.allocationId || index}-logs`}>
          <NoPadCell colSpan={4}>
            <TightLogs>
              <LogRowTable logs={logs} />
            </TightLogs>
          </NoPadCell>
        </Table.Row>
      )}
    </>
  );
}

export function PickingList({ data, loading, fallbackData, cancelAllocation }) {
  const hasMainData = Array.isArray(data) && data.length > 0;

  const rows = useMemo(() => (hasMainData ? data : []), [hasMainData, data]);

  const columns = [
    { text: 'Item', key: 'item', width: 8, textAlign: 'left' },
    { text: 'Location', key: 'location', width: 2, textAlign: 'left' },
    { text: 'Status', key: 'status', width: 4, textAlign: 'left' },
  ];

  const pickingList = useMemo(() => {
    if (hasMainData) return [];
    const src = fallbackData;
    const items = src?.items ?? [];

    if (src?.states?.[0] === OrderStatus.pendingFulfillment) {
      return items.filter(x => x.state === OrderStatus.pendingFulfillment);
    }
    return items;
  }, [hasMainData, fallbackData]);

  if (!hasMainData && !CommonUtility.isValidArray(pickingList)) {
    return null;
  }

  return (
    <div className="col-7 pl-0">
      <ShadowBox className={hasMainData ? 'p-3' : 'p-0'}>
        <ShadowBoxHeader className="pt-4">
          <Title>
            <ListChecks size={25} color="#ACADB6" className="mr-2" />
            Pick List
          </Title>
        </ShadowBoxHeader>

        {hasMainData ? (
          <TableWrapper>
            {data?.[0]?.wave?.waveNumber && (
              <RowJustified>
                <WaveID>
                  <Waves color={theme.colors.link} size={20} className="mr-2" />
                  {data[0]?.wave?.waveNumber}
                </WaveID>
                <Link
                  style={{ color: 'black' }}
                  to={`/app/fulfill/waves/${data[0]?.wave?.id}`}
                >
                  <WaveID>
                    <ArrowUpRight color={theme.colors.link} size={15} className="mr-2" />
                    Go to Wave
                  </WaveID>
                </Link>
              </RowJustified>
            )}
            <AppTable
              data={rows}
              loading={!!loading}
              RowComponent={<PickingRow cancelAllocation={cancelAllocation} />}
              columns={columns}
              keyField="allocationId"
              noShadow
            />
          </TableWrapper>
        ) : (
          <>
            {loading ? (
              <div className="p-4">
                <Placeholder fluid>
                  <PlaceholderParagraph>
                    <PlaceholderLine />
                  </PlaceholderParagraph>
                  <PlaceholderParagraph>
                    <PlaceholderLine />
                    <PlaceholderLine />
                    <PlaceholderLine />
                    <PlaceholderLine />
                  </PlaceholderParagraph>
                </Placeholder>
              </div>
            ) : (
              <>
                <ShadowBoxHeader className="pt-4">
                  <b>Pick List</b>
                </ShadowBoxHeader>

                <Header className="row">
                  <MuteText className="col-6">Item</MuteText>
                  <MuteText className="col-3">Location</MuteText>
                  <MuteText className="col-3">Wave</MuteText>
                </Header>

                <PickingItemsContainer>
                  {pickingList.map((item, index) => (
                    <React.Fragment key={item.item_id || index}>
                      <div className="row">
                        <div className="col-6 d-flex align-items-start">
                          <ItemImage size="mini" item={item} />
                          <ItemTitle className="ml-2">
                            <b>{item.title}</b>
                            <div className="d-flex">
                              <MuteText>SKU</MuteText>
                              <span className="ml-2">{item.sku}</span>
                            </div>
                          </ItemTitle>
                        </div>

                        <LocationsContainer className="col-3">
                          {CommonUtility.isValidArray(item.inventory) &&
                            item.inventory.map(inv => (
                              <div
                                key={inv?.location?.code}
                                className="d-flex flex-column"
                              >
                                <div className="d-flex align-items-center">
                                  <MapPin className="mr-2" />
                                  <span>{inv?.location?.code}</span>
                                </div>
                                <div className="d-flex">
                                  <MuteText>Available</MuteText>
                                  <span className="ml-2">{inv.quantity?.available}</span>
                                </div>
                              </div>
                            ))}
                        </LocationsContainer>

                        <LocationsContainer className="col-3">
                          {CommonUtility.isValidArray(item.allocations) &&
                            item.allocations.map((allocation, idx) => (
                              <div
                                key={allocation?._id || idx}
                                className="d-flex flex-column mb-2"
                              >
                                {allocation?.waveNumber ? (
                                  <Link to={`/app/fulfill/waves/${allocation.waveId}`}>
                                    <div className="d-flex align-items-center">
                                      <Waves className="mr-2" />
                                      <span>{allocation?.waveNumber}</span>
                                    </div>
                                  </Link>
                                ) : (
                                  <MuteText>Not able to be added to wave</MuteText>
                                )}

                                <div className="d-flex flex-column mt-1">
                                  <div className="d-flex">
                                    <MuteText>Quantity</MuteText>
                                    <span className="ml-2">{allocation.quantity}</span>
                                  </div>

                                  {idx === 0 && (
                                    <div className="d-flex">
                                      <MuteText>Status</MuteText>
                                      <span className="ml-2">
                                        {item.allocations.length === 1 ? (
                                          allocation.status.charAt(0).toUpperCase() +
                                          allocation.status.slice(1)
                                        ) : (
                                          <Popup
                                            content={
                                              <div style={{ whiteSpace: 'normal' }}>
                                                {item.allocations.map((a, i) => (
                                                  <div key={a._id || i}>
                                                    {item.title}:{' '}
                                                    <b>
                                                      {a.status.charAt(0).toUpperCase() +
                                                        a.status.slice(1)}
                                                    </b>
                                                  </div>
                                                ))}
                                              </div>
                                            }
                                            trigger={
                                              <span style={{ cursor: 'pointer' }}>
                                                Multiple
                                              </span>
                                            }
                                            position="bottom center"
                                            wide="very"
                                            mouseEnterDelay={100}
                                          />
                                        )}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                        </LocationsContainer>
                      </div>

                      {index + 1 !== pickingList.length && <Border />}
                    </React.Fragment>
                  ))}
                </PickingItemsContainer>
                <div className="pb-2" />
              </>
            )}
          </>
        )}
      </ShadowBox>
    </div>
  );
}

const TableWrapper = styled.div`
  padding: 10px;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.colors.blueFaded};
`;
const RowJustified = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const WaveID = styled.div`
  display: flex;
  align-items: center;
  font-size: 15px;
`;
const FileContainer = styled.a`
  display: flex;
  border: 1px solid ${({ theme }) => theme.colors.box};
  padding: 10px;
  border-radius: 5px;
  width: 100%;
  color: ${({ theme }) => theme.colors.primary};
  &:hover {
    color: ${({ theme }) => theme.colors.grey};
  }
`;

const TrackingId = styled.span`
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
  display: block;
`;

const CarrierLogo = styled(Image)`
  width: 30px;
  height: 40px;
  object-fit: contain;
  margin-right: 7px;
`;

const ItemTitle = styled.div`
  b {
    max-width: 15vw;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ItemContainer = styled.div`
  padding: 5px 0;
  &.showBorder {
    border-top: 1px solid ${({ theme }) => theme.colors.box};
  }
`;

const AvatarImg = styled.img`
  width: 25px;
  height: 25px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 6px;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
`;
const ItemName = styled.div`
  display: flex;
  align-items: flex-start;
`;
const ItemMeta = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 8px;
  min-width: 0;
`;
const SkuRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const UserBubble = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.black};
  color: ${({ theme }) => theme.colors.white};
  font-size: 12px;
  font-weight: 600;
  margin-right: 6px;
`;

const TightLogs = styled.div`
  margin-bottom: 10px;
  &&& .ui.table {
    margin: 0 !important;
  }

  &&& .ui.table thead,
  &&& .ui.table thead tr,
  &&& .ui.table thead th {
    border-bottom: 0 !important;
    box-shadow: none !important;
    border: 1px solid ${({ theme }) => theme.colors.box} !important;
  }
  &&& .ui.table thead th {
    border: 0 !important;
    box-shadow: none !important;
    font-weight: 400 !important;
    font-size: 12px;
    color: #adadadff !important;
  }
  &&& .ui.table thead th,
  &&& .ui.table tbody td {
    padding-top: 4px;
    padding-bottom: 4px;
  }
  &&& .ui.table tbody tr {
    height: 30px;
  }
  &&& .ui.table,
  &&& .ui.basic.table {
    margin: 0 !important;
    border: 1px solid ${({ theme }) => theme.colors.box} !important;
    box-shadow: none !important;
  }
`;

const NoPadCell = styled(Table.Cell)`
  &&& {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    border-top: 0 !important;
  }
`;
const PillLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: inline-flex;
  align-items: center;
`;
const PickingItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 17px;
`;
const LocationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
`;
